import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Divider,
  InputAdornment,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import PersonIcon from "@mui/icons-material/Person";
import UserService from "../pages/services/UserService";
import { fNumber, formatWithCommas, formatToTwoDecimals } from "../utils/formatNumber";

const parseAmount = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const clean = String(val).replace(/,/g, "").trim();
  const num = parseFloat(clean);
  return Number.isFinite(num) ? num : 0;
};

export default function BroughtForwardModal({
  open,
  onClose,
  members = [],
  preselectedMember = null,
  onSuccess
}) {
  const reduxUser = useSelector((state) => state.auth?.user);
  const viewAsRole = typeof sessionStorage !== "undefined" ? sessionStorage.getItem("mtc_view_as_role") : null;
  const userRoles = useMemo(() => {
    if (viewAsRole) return [viewAsRole.toLowerCase()];
    let raw = reduxUser?.roles || reduxUser?.role || [];
    try {
      if (!raw || (Array.isArray(raw) && raw.length === 0)) {
        const stored = JSON.parse(localStorage.getItem("user") || localStorage.getItem("profile"));
        raw = stored?.roles || stored?.role || [];
      }
    } catch {}
    const arr = Array.isArray(raw) ? raw : [raw];
    return arr.map((r) => (typeof r === "object" && r !== null ? r.name : String(r)).toLowerCase());
  }, [reduxUser, viewAsRole]);

  const isAdmin = userRoles.some((r) => r.includes("admin")) || Boolean(reduxUser?.isAdmin);
  const isBusinessOnly = userRoles.includes("business") && !isAdmin;

  const [tab, setTab] = useState(0);

  // Single Member Form State
  const [selectedMember, setSelectedMember] = useState(preselectedMember || null);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("Opening Balance (Brought Forward)");
  const [balances, setBalances] = useState({
    savings: "",
    shares: "",
    loan: "",
    ileya: "",
    dev: "",
    min: "",
    wel: "",
    takaful: "",
    speacc: "",
    business: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  // Bulk CSV State
  const [csvFile, setCsvFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkFeedback, setBulkFeedback] = useState(null);

  // Reset form when modal opens or preselectedMember changes
  React.useEffect(() => {
    if (open) {
      if (preselectedMember) {
        setSelectedMember(preselectedMember);
      }
      setFeedback(null);
      setBulkFeedback(null);
    }
  }, [open, preselectedMember]);

  const handleBalanceChange = (field, val) => {
    setBalances((prev) => ({ ...prev, [field]: val }));
  };

  // Compute live totals for single form using comma-safe parseAmount
  const totals = useMemo(() => {
    const business = parseAmount(balances.business);
    if (isBusinessOnly) {
      return { deposits: 0, loans: business, net: -business, business };
    }
    const savings = parseAmount(balances.savings);
    const shares = parseAmount(balances.shares);
    const ileya = parseAmount(balances.ileya);
    const dev = parseAmount(balances.dev);
    const min = parseAmount(balances.min);
    const wel = parseAmount(balances.wel);
    const takaful = parseAmount(balances.takaful);
    const speacc = parseAmount(balances.speacc);
    const deposits = savings + shares + ileya + dev + min + wel + takaful + speacc;

    const loan = parseAmount(balances.loan);
    const loans = loan + business;

    return { deposits, loans, net: deposits - loans, business };
  }, [balances, isBusinessOnly]);

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMember || (!selectedMember._id && !selectedMember.id)) {
      setFeedback({ severity: "error", message: "Please select a member first." });
      return;
    }

    const memberId = selectedMember._id || selectedMember.id;
    const numericBalances = {};
    let hasAnyAmount = false;
    const activeKeys = isBusinessOnly ? ["business"] : Object.keys(balances);
    activeKeys.forEach((k) => {
      const val = parseAmount(balances[k]);
      if (val > 0) {
        numericBalances[k] = val;
        hasAnyAmount = true;
      }
    });

    if (!hasAnyAmount) {
      setFeedback({
        severity: "warning",
        message: isBusinessOnly
          ? "Please enter a valid amount for the Business account."
          : "Please enter at least one opening balance amount greater than zero."
      });
      return;
    }

    setSubmitting(true);
    setFeedback(null);
    try {
      const res = await UserService.recordBroughtForward({
        memberId,
        date,
        description,
        balances: numericBalances
      });

      setSubmitting(false);
      setFeedback({
        severity: "success",
        message: res.data?.message || "Successfully recorded opening balances (Brought Forward)!"
      });

      // Clear balances
      setBalances({
        savings: "",
        shares: "",
        loan: "",
        ileya: "",
        dev: "",
        min: "",
        wel: "",
        takaful: "",
        speacc: "",
        business: ""
      });

      if (onSuccess) onSuccess();
    } catch (err) {
      setSubmitting(false);
      setFeedback({
        severity: "error",
        message: err?.response?.data?.error || err?.message || "Failed to record opening balances."
      });
    }
  };

  // CSV Parsing
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCsvFile(file);
    setBulkFeedback(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const text = evt.target.result;
        const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
        if (lines.length < 2) {
          setBulkFeedback({ severity: "error", message: "CSV file is empty or missing data rows." });
          return;
        }

        const headers = lines[0].split(',').map((h) => h.trim().replace(/^["']|["']$/g, '').toLowerCase());
        const rows = [];

        for (let i = 1; i < lines.length; i++) {
          const rowVals = lines[i].split(',').map((c) => c.trim().replace(/^["']|["']$/g, ''));
          if (rowVals.length === 0 || rowVals.every((v) => v === '')) continue;

          const rowObj = {};
          headers.forEach((h, idx) => {
            rowObj[h] = rowVals[idx] !== undefined ? rowVals[idx] : '';
          });
          rows.push(rowObj);
        }

        setParsedRows(rows);
      } catch (err) {
        setBulkFeedback({ severity: "error", message: "Could not parse CSV file. Ensure it is a valid CSV format." });
      }
    };
    reader.readAsText(file);
  };

  const handleBulkSubmit = async () => {
    if (!parsedRows || parsedRows.length === 0) {
      setBulkFeedback({ severity: "warning", message: "No parsed rows to upload." });
      return;
    }

    setBulkSubmitting(true);
    setBulkFeedback(null);
    try {
      // Sanitize amount strings so any comma-formatted figures (e.g. 20,000.45) convert to clean numbers
      const sanitizedRecords = parsedRows.map((row) => {
        const sanitized = { ...row };
        const amountKeys = ["savings", "shares", "loan", "ileya", "dev", "min", "wel", "takaful", "speacc", "business"];
        amountKeys.forEach((k) => {
          if (sanitized[k] !== undefined && sanitized[k] !== "") {
            sanitized[k] = parseAmount(sanitized[k]);
          }
        });
        return sanitized;
      });

      const res = await UserService.recordBulkBroughtForward({
        records: sanitizedRecords,
        date,
        description
      });

      setBulkSubmitting(false);
      setBulkFeedback({
        severity: "success",
        message: res.data?.message || `Successfully processed bulk Brought Forward for ${res.data?.results?.length || 0} members.`
      });
      setParsedRows([]);
      setCsvFile(null);

      if (onSuccess) onSuccess();
    } catch (err) {
      setBulkSubmitting(false);
      setBulkFeedback({
        severity: "error",
        message: err?.response?.data?.error || err?.message || "Failed to process bulk Brought Forward."
      });
    }
  };

  const downloadTemplate = () => {
    const headers = isBusinessOnly
      ? ["registrationNo", "fullName", "business"]
      : [
          "registrationNo",
          "fullName",
          "savings",
          "shares",
          "loan",
          "ileya",
          "dev",
          "min",
          "wel",
          "takaful",
          "speacc",
          "business"
        ];
    const lines = [headers.join(",")];
    if (members && members.length > 0) {
      members.slice(0, 10).forEach((m) => {
        const reg = m.registrationNo || m.membershipId || "";
        const name = `"${[m.firstName, m.lastName].filter(Boolean).join(" ").trim() || m.username}"`;
        lines.push(isBusinessOnly ? `${reg},${name},0` : `${reg},${name},0,0,0,0,0,0,0,0,0,0`);
      });
    } else {
      lines.push(isBusinessOnly ? '100001,"Sample Member",50000' : '100001,"Sample Member",50000,20000,0,10000,5000,1000,2000,0,0,0');
    }

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = isBusinessOnly ? "business_brought_forward_template.csv" : "brought_forward_opening_balances_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const allAccountFields = [
    { key: "savings", label: "Savings Deposit (₦)", color: "#0B3B60" },
    { key: "shares", label: "Shares Deposit (₦)", color: "#0B3B60" },
    { key: "loan", label: "Loan Debt Balance (₦)", color: "#D97706" },
    { key: "ileya", label: "Ileya Savings (₦)", color: "#0B3B60" },
    { key: "dev", label: "Development Levy (₦)", color: "#0B3B60" },
    { key: "min", label: "Minutes Levy (₦)", color: "#0B3B60" },
    { key: "wel", label: "Welfare Levy (₦)", color: "#0B3B60" },
    { key: "takaful", label: "Takaful Contribution (₦)", color: "#0B3B60" },
    { key: "speacc", label: "Special Account (₦)", color: "#0B3B60" },
    { key: "business", label: "Business Debt Balance (₦)", color: "#0D9488" }
  ];

  const accountFields = isBusinessOnly
    ? [{ key: "business", label: "Business Debt / Disbursed Balance (₦)", color: "#0D9488" }]
    : allAccountFields;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#0B3B60",
          color: "#ffffff",
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 },
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <AccountBalanceWalletIcon sx={{ fontSize: { xs: 22, sm: 26 } }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: { xs: "0.92rem", sm: "1.1rem" }, lineHeight: 1.25 }}>
              {isBusinessOnly
                ? "One-Time Deposit — Brought Forward (Business Account Only)"
                : "One-Time Deposit — Brought Forward (Opening Balances)"}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", display: { xs: "none", sm: "block" } }}>
              {isBusinessOnly
                ? "Record opening balance on member Business account"
                : "Initialize member balances across all cooperative accounts at once"}
            </Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "#ffffff", ml: 1 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f8fafc", px: { xs: 1, sm: 3 } }}>
        <Tabs value={tab} onChange={(_, val) => setTab(val)} variant="scrollable" scrollButtons="auto">
          <Tab icon={<PersonIcon />} iconPosition="start" label="Single Member Entry" sx={{ textTransform: "none", fontWeight: 700 }} />
          <Tab icon={<UploadFileIcon />} iconPosition="start" label="Bulk CSV Import" sx={{ textTransform: "none", fontWeight: 700 }} />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Tab 0: Single Member Entry */}
        {tab === 0 && (
          <Box component="form" onSubmit={handleSingleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {feedback && (
              <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}

            <Grid container spacing={2}>
              {/* Member Selector */}
              <Grid item xs={12} sm={7}>
                <Autocomplete
                  options={members}
                  isOptionEqualToValue={(option, value) => (option?._id || option?.id) === (value?._id || value?.id)}
                  getOptionLabel={(opt) => {
                    if (!opt) return "";
                    const name = [opt.firstName, opt.lastName].filter(Boolean).join(" ").trim() || opt.username || "Member";
                    const reg = opt.registrationNo || opt.membershipId || "";
                    return `${name} ${reg ? `(${reg})` : ""}`;
                  }}
                  value={selectedMember}
                  onChange={(_, val) => setSelectedMember(val)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Member *"
                      placeholder="Search member by name or reg #"
                      required
                    />
                  )}
                />
              </Grid>

              {/* Date */}
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Opening Balance Date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Transaction Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 0.5 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>
                Enter Initial Balances (₦)
              </Typography>
            </Divider>

            {/* 10 Account Input Fields with thousands comma formatting (e.g. 20,000.45) */}
            <Grid container spacing={2}>
              {accountFields.map((field) => (
                <Grid item xs={12} sm={6} key={field.key}>
                  <TextField
                    fullWidth
                    size="small"
                    label={field.label}
                    placeholder="0.00"
                    value={balances[field.key]}
                    onChange={(e) => handleBalanceChange(field.key, formatWithCommas(e.target.value))}
                    onBlur={() => {
                      if (balances[field.key]) {
                        handleBalanceChange(field.key, formatToTwoDecimals(balances[field.key]));
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ color: "#64748B", fontWeight: 700 }}>
                          ₦
                        </InputAdornment>
                      ),
                      inputMode: "decimal"
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        backgroundColor: "#F8FAFC"
                      }
                    }}
                  />
                </Grid>
              ))}
            </Grid>

            {/* Summary Card */}
            <Card variant="outlined" sx={{ bgcolor: "#F0F9FF", borderColor: "#BAE6FD", borderRadius: 2 }}>
              <CardContent sx={{ py: "12px !important" }}>
                {isBusinessOnly ? (
                  <Typography variant="body2" sx={{ color: "#0D9488", fontWeight: 700 }}>
                    Total Business Debt Brought Forward: <strong>₦{fNumber(parseAmount(balances.business))}</strong>
                  </Typography>
                ) : (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ color: "#0369A1", fontWeight: 700 }}>
                        Total Deposits Brought Forward: <strong>₦{fNumber(totals.deposits)}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ textAlign: { sm: "right" } }}>
                      <Typography variant="body2" sx={{ color: "#B45309", fontWeight: 700 }}>
                        Total Loans / Debt Brought Forward: <strong>₦{fNumber(totals.loans)}</strong>
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 1,
                flexDirection: { xs: "column-reverse", sm: "row" }
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={submitting}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={submitting || !selectedMember}
                sx={{
                  bgcolor: "#0B3B60",
                  fontWeight: 700,
                  px: 3,
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: "#072740" }
                }}
              >
                {submitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Record Brought Forward"}
              </Button>
            </Box>
          </Box>
        )}

        {/* Tab 1: Bulk CSV Import */}
        {tab === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {bulkFeedback && (
              <Alert severity={bulkFeedback.severity} onClose={() => setBulkFeedback(null)}>
                {bulkFeedback.message}
              </Alert>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: "#64748B" }}>
                Upload a spreadsheet (CSV) containing Brought Forward balances for all cooperative members.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
              >
                Download CSV Template
              </Button>
            </Box>

            <Box
              sx={{
                border: "2px dashed #CBD5E1",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                bgcolor: "#F8FAFC",
                cursor: "pointer",
                "&:hover": { borderColor: "#0B3B60", bgcolor: "#F1F5F9" }
              }}
              component="label"
            >
              <input type="file" accept=".csv" hidden onChange={handleCSVUpload} />
              <UploadFileIcon sx={{ fontSize: 40, color: "#0B3B60", mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#0F172A" }}>
                {csvFile ? csvFile.name : "Click or drag CSV file here to upload"}
              </Typography>
              <Typography variant="caption" sx={{ color: "#64748B" }}>
                Supports standard CSV with member registration numbers and account columns
              </Typography>
            </Box>

            {/* Parsed Rows Preview */}
            {parsedRows.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: "#0F172A" }}>
                  Preview: {parsedRows.length} member records found
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 240 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Reg No</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        {isBusinessOnly ? (
                          <TableCell align="right" sx={{ fontWeight: 700 }}>Business</TableCell>
                        ) : (
                          <>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Savings</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Shares</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Loan</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Ileya</TableCell>
                          </>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedRows.slice(0, 10).map((r, i) => (
                        <TableRow key={i}>
                          <TableCell>{r.registrationno || r.regno || r.membershipid || "—"}</TableCell>
                          <TableCell>{r.fullname || r.name || r.username || "—"}</TableCell>
                          {isBusinessOnly ? (
                            <TableCell align="right">₦{fNumber(parseAmount(r.business))}</TableCell>
                          ) : (
                            <>
                              <TableCell align="right">₦{fNumber(parseAmount(r.savings))}</TableCell>
                              <TableCell align="right">₦{fNumber(parseAmount(r.shares))}</TableCell>
                              <TableCell align="right">₦{fNumber(parseAmount(r.loan))}</TableCell>
                              <TableCell align="right">₦{fNumber(parseAmount(r.ileya))}</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {parsedRows.length > 10 && (
                  <Typography variant="caption" sx={{ color: "#64748B", mt: 0.5, display: "block" }}>
                    Showing first 10 rows of {parsedRows.length} total.
                  </Typography>
                )}
              </Box>
            )}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
                mt: 1,
                flexDirection: { xs: "column-reverse", sm: "row" }
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={bulkSubmitting}
                sx={{ width: { xs: "100%", sm: "auto" } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleBulkSubmit}
                disabled={bulkSubmitting || parsedRows.length === 0}
                sx={{
                  bgcolor: "#0B3B60",
                  fontWeight: 700,
                  px: 3,
                  width: { xs: "100%", sm: "auto" },
                  "&:hover": { bgcolor: "#072740" }
                }}
              >
                {bulkSubmitting ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : `Apply ${parsedRows.length} Opening Balances`}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
